"use client"

import { useState, useRef } from "react"
import { Upload, Download, HelpCircle, CheckCircle, AlertTriangle, Trash2, FileSpreadsheet, AlertCircle, ArrowRight, ArrowLeft, Package, List, CheckSquare } from 'lucide-react'
import * as XLSX from 'xlsx'
import { authFetch } from "../utils/authFetch";

const BulkUploadModal = ({ isOpen, onClose, onSuccess, API_URL, token }) => {
  const [currentStep, setCurrentStep] = useState(1) // 1: upload, 2: preview, 3: confirm, 4: results
  // eslint-disable-next-line no-unused-vars
  const [file, setFile] = useState(null)
  const [previewData, setPreviewData] = useState([])
  const [validationErrors, setValidationErrors] = useState({})
  // eslint-disable-next-line no-unused-vars
  const [isProcessing, setIsProcessing] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [uploadResults, setUploadResults] = useState(null)
  const fileInputRef = useRef(null)

  const requiredFields = ['barcode', 'lote', 'fecha_vencimiento', 'proveedor']

  const expectedColumns = [
    { key: 'barcode', label: 'Código de Barras', required: true },
    { key: 'cod_articulo', label: 'Código Artículo', required: false },
    { key: 'descripcion', label: 'Descripción', required: false },
    { key: 'proveedor', label: 'Proveedor', required: true },
    { key: 'unidad_negocio_proveedor', label: 'Unidad Negocio', required: false },
    { key: 'codigo_articulo_proveedor', label: 'Código Artículo Prov', required: false },
    { key: 'lote', label: 'Número de Lote', required: true },
    { key: 'fecha_compra', label: 'Fecha Compra', required: false },
    { key: 'fecha_vencimiento', label: 'Fecha Vencimiento', required: true },
    { key: 'familia', label: 'Familia', required: false },
    { key: 'subfamilia', label: 'SubFamilia', required: false },
    { key: 'categoria', label: 'Categoría', required: true },
    { key: 'subcategoria', label: 'SubCategoría', required: false },
    { key: 'unidad_max', label: 'Unidad Mayor', required: false },
    { key: 'unidad_med', label: 'Unidad Media', required: false },
    { key: 'unidad_peq', label: 'Unidad Pequeña', required: false },
    { key: 'unidad_maxima_actual', label: 'Und. Max Actual', required: false },
    { key: 'unidad_media_actual', label: 'Und. Med Actual', required: false },
    { key: 'unidad_minima_actual', label: 'Und. Min Actual', required: false },
    { key: 'cantidad_max', label: 'Cantidad Mayor', required: false },
    { key: 'cantidad_med', label: 'Cantidad Media', required: false },
    { key: 'cantidad_peq', label: 'Cantidad Pequeña', required: false },
    { key: 'cantidad_display_por_unidad_mayor', label: 'Conv. Med/May', required: false },
    { key: 'cantidad_unidad_base_por_display', label: 'Conv. Peq/Med', required: false },
    { key: 'min_stock', label: 'Stock Mínimo', required: false },
    { key: 'peso', label: 'Peso (kg)', required: false },
    { key: 'volumen', label: 'Volumen (cm³)', required: false },
    { key: 'costo_real', label: 'Costo Real', required: false },
    { key: 'costo_promedio', label: 'Costo Promedio', required: false },
    { key: 'price', label: 'Precio Venta', required: false },
    { key: 'cod_almacen', label: 'Código Almacén', required: false },
    { key: 'imagen_url', label: 'URL Imagen', required: false }
  ]

  const downloadTemplate = () => {
    const templateData = [
      {
        barcode: '7501234567890',
        cod_articulo: 'ART-001',
        descripcion: 'Producto Ejemplo - Caja de 5 Displays',
        proveedor: 'Proveedor A',
        unidad_negocio_proveedor: 'UN-001',
        codigo_articulo_proveedor: 'PROV-001',
        lote: 'LOTE-2024-001',
        fecha_compra: '2024-01-15',
        fecha_vencimiento: '2025-12-31',
        familia: 'Alimentos',
        subfamilia: 'Snacks',
        categoria: 'Lácteos',
        subcategoria: 'Yogurt',
        unidad_max: 'Caja',
        unidad_med: 'Display',
        unidad_peq: 'Unidad',
        unidad_maxima_actual: 'Caja',
        unidad_media_actual: 'Display',
        unidad_minima_actual: 'Unidad',
        cantidad_max: '2',
        cantidad_med: '3',
        cantidad_peq: '5',
        cantidad_display_por_unidad_mayor: '5',
        cantidad_unidad_base_por_display: '5',
        min_stock: '10',
        peso: '0.5',
        volumen: '500',
        costo_real: '10.50',
        costo_promedio: '10.75',
        price: '15.00',
        cod_almacen: '01',
        imagen_url: 'https://ejemplo.com/imagen.jpg'
      },
      {
        barcode: '7501234567890',
        cod_articulo: 'ART-001',
        descripcion: 'Producto Ejemplo - Segundo Lote',
        proveedor: 'Proveedor A',
        unidad_negocio_proveedor: 'UN-001',
        codigo_articulo_proveedor: 'PROV-001',
        lote: 'LOTE-2024-002',
        fecha_compra: '2024-02-10',
        fecha_vencimiento: '2025-06-30',
        familia: 'Alimentos',
        subfamilia: 'Snacks',
        categoria: 'Lácteos',
        subcategoria: 'Yogurt',
        unidad_max: 'Caja',
        unidad_med: 'Display',
        unidad_peq: 'Unidad',
        unidad_maxima_actual: 'Paquete Grande',
        unidad_media_actual: 'Paquete Pequeño',
        unidad_minima_actual: 'Pieza',
        cantidad_max: '1',
        cantidad_med: '0',
        cantidad_peq: '12',
        cantidad_display_por_unidad_mayor: '5',
        cantidad_unidad_base_por_display: '5',
        min_stock: '10',
        peso: '0.5',
        volumen: '500',
        costo_real: '10.50',
        costo_promedio: '10.75',
        price: '15.00',
        cod_almacen: '01',
        imagen_url: 'https://ejemplo.com/imagen.jpg'
      },
      {
        barcode: '7501234567891',
        cod_articulo: 'ART-002',
        descripcion: 'Producto Ejemplo 2',
        proveedor: 'Proveedor B',
        unidad_negocio_proveedor: 'UN-002',
        codigo_articulo_proveedor: 'PROV-002',
        lote: 'LOTE-2024-003',
        fecha_compra: '2024-01-20',
        fecha_vencimiento: '2025-06-30',
        familia: 'Bebidas',
        subfamilia: '',
        categoria: 'Alimentos y Bebidas',
        subcategoria: '',
        unidad_max: 'Caja',
        unidad_med: 'Paquete',
        unidad_peq: 'Unidad',
        unidad_maxima_actual: 'Caja',
        unidad_media_actual: 'Paquete',
        unidad_minima_actual: 'Unidad',
        cantidad_max: '0',
        cantidad_med: '10',
        cantidad_peq: '3',
        cantidad_display_por_unidad_mayor: '10',
        cantidad_unidad_base_por_display: '6',
        min_stock: '5',
        peso: '1.0',
        volumen: '1000',
        costo_real: '20.00',
        costo_promedio: '20.50',
        price: '30.00',
        cod_almacen: '02',
        imagen_url: ''
      }
    ]

    const ws = XLSX.utils.json_to_sheet(templateData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Lotes')
    
    const colWidths = expectedColumns.map(col => ({ wch: 20 }))
    ws['!cols'] = colWidths

    XLSX.writeFile(wb, 'plantilla_lotes_productos.xlsx')
  }

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0]
    if (!selectedFile) return

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ]

    if (!validTypes.includes(selectedFile.type)) {
      alert('Por favor seleccione un archivo Excel válido (.xlsx, .xls)')
      return
    }

    setFile(selectedFile)
    parseExcelFile(selectedFile)
  }

  const parseExcelFile = (file) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
        const jsonData = XLSX.utils.sheet_to_json(firstSheet)

        if (jsonData.length === 0) {
          alert('El archivo Excel está vacío')
          return
        }

        const normalizedData = jsonData.map((row, index) => {
          const normalizedRow = { _rowIndex: index }
          Object.keys(row).forEach(key => {
            const normalizedKey = key.toLowerCase().trim().replace(/\s+/g, '_')
            normalizedRow[normalizedKey] = row[key]
          })
          return normalizedRow
        })

        setPreviewData(normalizedData)
        validateData(normalizedData)
        setCurrentStep(2)
      } catch (error) {
        console.error('Error parsing Excel:', error)
        alert('Error al leer el archivo Excel: ' + error.message)
      }
    }

    reader.readAsArrayBuffer(file)
  }

  const validateData = (data) => {
    const errors = {}

    data.forEach((row, index) => {
      const rowErrors = []

      requiredFields.forEach(field => {
        if (!row[field] || row[field].toString().trim() === '') {
          rowErrors.push(`Campo requerido: ${field}`)
        }
      })

      const cantidadMax = parseInt(row.cantidad_max) || 0;
      const cantidadMed = parseInt(row.cantidad_med) || 0;
      const cantidadPeq = parseInt(row.cantidad_peq) || 0;

      if (cantidadMax === 0 && cantidadMed === 0 && cantidadPeq === 0) {
        rowErrors.push('Al menos una cantidad (max, med, peq) debe ser mayor a 0')
      }

      if (row.fecha_vencimiento) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/
        if (!dateRegex.test(row.fecha_vencimiento)) {
          rowErrors.push('Fecha vencimiento debe estar en formato YYYY-MM-DD')
        }
      }

      if (row.fecha_compra && row.fecha_compra.toString().trim() !== '') {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/
        if (!dateRegex.test(row.fecha_compra)) {
          rowErrors.push('Fecha compra debe estar en formato YYYY-MM-DD')
        }
      }

      const numericFields = ['cantidad_max', 'cantidad_med', 'cantidad_peq', 'cantidad_display_por_unidad_mayor', 'cantidad_unidad_base_por_display', 'min_stock', 'peso', 'volumen', 'costo_real', 'costo_promedio', 'price']
      numericFields.forEach(field => {
        if (row[field] && row[field].toString().trim() !== '') {
          if (isNaN(parseFloat(row[field]))) {
            rowErrors.push(`${field} debe ser un número válido`)
          }
        }
      })

      if (rowErrors.length > 0) {
        errors[index] = rowErrors
      }
    })

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleCellEdit = (rowIndex, field, value) => {
    const updatedData = [...previewData]
    updatedData[rowIndex][field] = value
    setPreviewData(updatedData)
    validateData(updatedData)
  }

  const handleDeleteRow = (rowIndex) => {
    const updatedData = previewData.filter((_, index) => index !== rowIndex)
    setPreviewData(updatedData)
    validateData(updatedData)
  }

  const calcularStockFila = (row) => {
    const cantidadMax = parseInt(row.cantidad_max) || 0;
    const cantidadMed = parseInt(row.cantidad_med) || 0;
    const cantidadPeq = parseInt(row.cantidad_peq) || 0;
    
    const conversionMed = parseInt(row.cantidad_display_por_unidad_mayor) || 1;
    const conversionPeq = parseInt(row.cantidad_unidad_base_por_display) || 1;
    
    return (cantidadMax * conversionMed * conversionPeq) + (cantidadMed * conversionPeq) + cantidadPeq;
  };

  const handleUpload = async () => {
    if (Object.keys(validationErrors).length > 0) {
      alert('Por favor corrija los errores antes de continuar')
      return
    }

    setIsProcessing(true)
    setCurrentStep(3)

    try {
      console.log('🚀 Enviando datos al backend:', previewData)
      console.log('📍 URL:', `${API_URL}/products/bulk-upload`)
      
      const response = await authFetch(`${API_URL}/products/bulk-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ products: previewData })
      })

      if (!response) return;
      console.log('📥 Response status:', response.status)
      const result = await response.json()
      console.log('📦 Response data:', result)
      console.log('📊 Created:', result.data?.created) // ← AÑADIR ESTE
      console.log('📊 Updated:', result.data?.updated) // ← AÑADIR ESTE
      console.log('❌ Errors:', result.data?.errors)   // ← AÑADIR ESTE

      if (result.success) {
        setUploadResults(result.data)
        setCurrentStep(4)
        
        if (onSuccess) {
          onSuccess()
        }
      } else {
        console.error('❌ Error del servidor:', result.error)
        alert('❌ Error: ' + result.error)
        setCurrentStep(2)
      }
    } catch (error) {
      console.error('❌ Error uploading products:', error)
      alert('❌ Error al subir productos: ' + error.message)
      setCurrentStep(2)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    setCurrentStep(1)
    setFile(null)
    setPreviewData([])
    setValidationErrors({})
    setShowHelp(false)
    setUploadResults(null)
    onClose()
  }

  const handleReset = () => {
    setCurrentStep(1)
    setFile(null)
    setPreviewData([])
    setValidationErrors({})
    setUploadResults(null)
  }

  if (!isOpen) return null

  const productosAgrupados = previewData.reduce((acc, row) => {
    const barcode = row.barcode;
    if (!acc[barcode]) {
      acc[barcode] = {
        barcode,
        descripcion: row.descripcion,
        lotes: [],
        stockTotal: 0
      };
    }
    const stockLote = calcularStockFila(row);
    acc[barcode].lotes.push({ lote: row.lote, stock: stockLote });
    acc[barcode].stockTotal += stockLote;
    return acc;
  }, {});

  // eslint-disable-next-line no-unused-vars
  const steps = [
    { number: 1, title: 'Cargar Archivo', icon: Upload },
    { number: 2, title: 'Revisar y Editar', icon: List },
    { number: 3, title: 'Procesando', icon: CheckSquare },
    { number: 4, title: 'Resultados', icon: CheckCircle }
  ]

  return (
    <div className="bulk-upload-page">
      {/* Content Area */}
      <div className="bulk-content">
        {/* STEP 1: Upload */}
        {currentStep === 1 && (
          <div className="step-content step-upload">
            <div className="upload-container">
              <div className="upload-header">
                <FileSpreadsheet size={32} className="upload-icon" />
                <h2>Carga Masiva de Lotes</h2>
                <p>Sube tu archivo Excel con los lotes de productos</p>
              </div>

              <div className="help-toggle">
                <button onClick={() => setShowHelp(!showHelp)} className="help-btn-inline">
                  <HelpCircle size={16} />
                  {showHelp ? 'Ocultar' : 'Ver'} Guía de Uso
                </button>
              </div>

              {showHelp && (
                <div className="help-panel-inline">
                  <div className="help-grid">
                    <div className="help-card">
                      <h4>¿Qué es un Lote?</h4>
                      <p>Cada fila del Excel representa UN LOTE de producto. Un producto puede tener múltiples lotes con diferentes fechas de vencimiento.</p>
                    </div>
                    <div className="help-card">
                      <h4>Campos Requeridos</h4>
                      <ul>
                        <li>barcode</li>
                        <li>lote</li>
                        <li>fecha_vencimiento</li>
                        <li>proveedor</li>
                        <li>Al menos una cantidad</li>
                      </ul>
                    </div>
                    <div className="help-card">
                      <h4>Jerarquía de Unidades</h4>
                      <p><strong>Mayor:</strong> Caja<br/>
                      <strong>Media:</strong> Display<br/>
                      <strong>Pequeña:</strong> Unidad</p>
                      <p className="help-example">Ej: 1 Caja = 5 Displays = 25 Unidades</p>
                    </div>
                    <div className="help-card">
                      <h4>Múltiples Lotes</h4>
                      <p>Si hay varias filas con el mismo barcode, se crearán múltiples lotes para ese producto.</p>
                    </div>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="file-input-hidden"
              />

              <div className="upload-zone" onClick={() => fileInputRef.current?.click()}>
                <Upload size={48} />
                <h3>Haz clic para seleccionar</h3>
                <p>o arrastra tu archivo aquí</p>
                <span className="file-types">Formatos: .xlsx, .xls</span>
              </div>

              <div className="actions-row">
                <button className="btn-template" onClick={downloadTemplate}>
                  <Download size={16} />
                  Descargar Plantilla
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Preview & Edit */}
        {currentStep === 2 && (
          <div className="step-content step-preview">
            <div className="preview-header-section">
              <div className="preview-title">
                <List size={24} />
                <div>
                  <h2>Revisar y Editar Datos</h2>
                  <p>Verifica la información antes de procesar</p>
                </div>
              </div>
              <div className="preview-stats">
                <div className="stat-card">
                  <span className="stat-label">Lotes</span>
                  <span className="stat-value">{previewData.length}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Productos</span>
                  <span className="stat-value">{Object.keys(productosAgrupados).length}</span>
                </div>
                <div className="stat-card error">
                  <span className="stat-label">Errores</span>
                  <span className="stat-value">{Object.keys(validationErrors).length}</span>
                </div>
              </div>
            </div>

            {Object.keys(validationErrors).length > 0 && (
              <div className="validation-alert">
                <AlertTriangle size={18} />
                <span>Hay {Object.keys(validationErrors).length} filas con errores que deben corregirse</span>
              </div>
            )}

            <div className="products-summary-section">
              <h3>Resumen por Producto</h3>
              <div className="summary-grid">
                {Object.values(productosAgrupados).map((prod, idx) => (
                  <div key={idx} className="summary-card">
                    <div className="summary-header">
                      <span className="summary-barcode">{prod.barcode}</span>
                      <span className="summary-badge">{prod.lotes.length} lote(s)</span>
                    </div>
                    <div className="summary-desc">{prod.descripcion || 'Sin descripción'}</div>
                    <div className="summary-footer">
                      <span>Stock Total:</span>
                      <strong>{prod.stockTotal} uds</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="table-section">
              <div className="table-header-bar">
                <h3>Detalle de Lotes</h3>
                <button className="btn-change-file" onClick={handleReset}>
                  <Upload size={14} />
                  Cambiar Archivo
                </button>
              </div>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="col-status">#</th>
                      {expectedColumns.slice(0, 15).map(col => (
                        <th key={col.key} className={col.required ? 'required-col' : ''}>
                          {col.label}
                          {col.required && <span className="required-mark">*</span>}
                        </th>
                      ))}
                      <th className="col-stock">Stock</th>
                      <th className="col-actions">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, rowIndex) => (
                      <tr key={rowIndex} className={validationErrors[rowIndex] ? 'row-error' : ''}>
                        <td className="cell-status">
                          {validationErrors[rowIndex] ? (
                            <div className="status-error" title={validationErrors[rowIndex].join(', ')}>
                              <AlertCircle size={14} />
                              {rowIndex + 1}
                            </div>
                          ) : (
                            <div className="status-success">
                              <CheckCircle size={14} />
                              {rowIndex + 1}
                            </div>
                          )}
                        </td>
                        {expectedColumns.slice(0, 15).map(col => (
                          <td key={col.key}>
                            <input
                              type="text"
                              value={row[col.key] || ''}
                              onChange={(e) => handleCellEdit(rowIndex, col.key, e.target.value)}
                              className="cell-input"
                            />
                          </td>
                        ))}
                        <td className="cell-stock">
                          <span className="stock-badge">{calcularStockFila(row)}</span>
                        </td>
                        <td className="cell-actions">
                          <button
                            className="btn-delete-row"
                            onClick={() => handleDeleteRow(rowIndex)}
                            title="Eliminar"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {validationErrors && Object.keys(validationErrors).length > 0 && (
              <div className="errors-section">
                <h3>Lista de Errores</h3>
                <div className="errors-list">
                  {Object.entries(validationErrors).map(([rowIndex, errors]) => (
                    <div key={rowIndex} className="error-card">
                      <strong>Fila {parseInt(rowIndex) + 1}:</strong>
                      <ul>
                        {errors.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="step-actions">
              <button className="btn-secondary" onClick={handleReset}>
                <ArrowLeft size={16} />
                Volver
              </button>
              <button
                className="btn-primary"
                onClick={handleUpload}
                disabled={Object.keys(validationErrors).length > 0}
              >
                Procesar Lotes
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Processing */}
        {currentStep === 3 && (
          <div className="step-content step-processing">
            <div className="processing-container">
              <div className="processing-spinner">
                <div className="spinner-ring"></div>
                <CheckSquare size={32} />
              </div>
              <h2>Procesando Lotes...</h2>
              <p>Esto puede tomar unos momentos, por favor espere.</p>
            </div>
          </div>
        )}

        {/* STEP 4: Results */}
        {currentStep === 4 && uploadResults && (
          <div className="step-content step-results">
            <div className="results-container">
              <div className="results-header">
                <div className="success-icon">
                  <CheckCircle size={48} />
                </div>
                <h2>¡Carga Completada!</h2>
                <p>Los lotes han sido procesados exitosamente</p>
              </div>

              <div className="results-stats">
                <div className="stat-box success">
                  <span className="stat-number">{uploadResults.created}</span>
                  <span className="stat-label">Productos Creados</span>
                </div>
                <div className="stat-box info">
                  <span className="stat-number">{uploadResults.updated}</span>
                  <span className="stat-label">Productos Actualizados</span>
                </div>
                <div className="stat-box primary">
                  <span className="stat-number">{previewData.length}</span>
                  <span className="stat-label">Lotes Procesados</span>
                </div>
                {uploadResults.errors && uploadResults.errors.length > 0 && (
                  <div className="stat-box error">
                    <span className="stat-number">{uploadResults.errors.length}</span>
                    <span className="stat-label">Errores</span>
                  </div>
                )}
              </div>

              <div className="results-details">
                <h3>Detalle de Lotes Añadidos</h3>
                <div className="lotes-results-grid">
                  {Object.values(productosAgrupados).map((prod, idx) => (
                    <div key={idx} className="lote-result-card">
                      <div className="lote-result-header">
                        <Package size={18} />
                        <span className="lote-barcode">{prod.barcode}</span>
                      </div>
                      <div className="lote-result-body">
                        <p className="lote-desc">{prod.descripcion || 'Sin descripción'}</p>
                        <div className="lote-stats">
                          <div className="lote-stat">
                            <span>Lotes:</span>
                            <strong>{prod.lotes.length}</strong>
                          </div>
                          <div className="lote-stat">
                            <span>Stock:</span>
                            <strong>{prod.stockTotal} uds</strong>
                          </div>
                        </div>
                        <div className="lote-list">
                          {prod.lotes.map((lote, loteIdx) => (
                            <div key={loteIdx} className="lote-item">
                              <CheckCircle size={12} />
                              <span>{lote.lote}</span>
                              <span className="lote-stock">{lote.stock} uds</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {uploadResults.errors && uploadResults.errors.length > 0 && (
                <div className="errors-section">
                  <h3>Errores Encontrados</h3>
                  <div className="errors-list">
                    {uploadResults.errors.map((error, idx) => (
                      <div key={idx} className="error-card">
                        <AlertTriangle size={16} />
                        <span>{error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="results-actions">
                <button className="btn-secondary" onClick={handleReset}>
                  <Upload size={16} />
                  Nueva Carga
                </button>
                <button className="btn-primary" onClick={handleClose}>
                  <CheckCircle size={16} />
                  Finalizar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .bulk-upload-page {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #F9FAFB;
        }

        /* Steps Header */
        .steps-header {
          background: white;
          border-bottom: 1px solid #E5E7EB;
          padding: 24px;
        }

        .steps-container {
          display: flex;
          align-items: center;
          max-width: 900px;
          margin: 0 auto;
        }

        .step-wrapper {
          display: flex;
          align-items: center;
          flex: 1;
        }

        .step-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .step-item.active {
          background: rgba(59, 130, 246, 0.1);
        }

        .step-item.completed {
          background: rgba(16, 185, 129, 0.1);
        }

        .step-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #F1F5F9;
          color: #94A3B8;
          transition: all 0.3s ease;
        }

        .step-item.active .step-icon {
          background: #3B82F6;
          color: white;
        }

        .step-item.completed .step-icon {
          background: #10B981;
          color: white;
        }

        .step-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .step-number {
          font-size: 11px;
          color: #94A3B8;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .step-title {
          font-size: 14px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .step-connector {
          flex: 1;
          height: 2px;
          background: #E5E7EB;
          margin: 0 16px;
          position: relative;
        }

        .step-connector.completed {
          background: #10B981;
        }

        /* Content Area */
        .bulk-content {
          flex: 1;
          overflow: auto;
          padding: 24px;
        }

        .step-content {
          max-width: 1400px;
          margin: 0 auto;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* STEP 1: Upload */
        .step-upload .upload-container {
          max-width: 800px;
          margin: 0 auto;
          background: white;
          border-radius: 12px;
          padding: 32px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .upload-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .upload-icon {
          color: #3B82F6;
          margin-bottom: 16px;
        }

        .upload-header h2 {
          margin: 0 0 8px 0;
          font-size: 24px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .upload-header p {
          margin: 0;
          font-size: 14px;
          color: #64748B;
        }

        .help-toggle {
          margin-bottom: 24px;
          text-align: center;
        }

        .help-btn-inline {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          background: white;
          color: #64748B;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .help-btn-inline:hover {
          background: #F9FAFB;
          border-color: #3B82F6;
          color: #3B82F6;
        }

        .help-panel-inline {
          margin-bottom: 32px;
          padding: 24px;
          background: #F9FAFB;
          border-radius: 12px;
          border: 1px solid #E5E7EB;
        }

        .help-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }

        .help-card {
          padding: 16px;
          background: white;
          border-radius: 8px;
          border: 1px solid #E5E7EB;
        }

        .help-card h4 {
          margin: 0 0 12px 0;
          font-size: 14px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .help-card p {
          margin: 0 0 8px 0;
          font-size: 13px;
          color: #64748B;
          line-height: 1.6;
        }

        .help-card ul {
          margin: 0;
          padding-left: 20px;
          font-size: 13px;
          color: #64748B;
        }

        .help-card li {
          margin-bottom: 4px;
        }

        .help-example {
          padding: 8px 12px;
          background: #F9FAFB;
          border-radius: 6px;
          font-family: monospace;
          font-size: 12px;
        }

        .file-input-hidden {
          display: none;
        }

        .upload-zone {
          border: 2px dashed #E5E7EB;
          border-radius: 12px;
          padding: 64px 32px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #FAFAFA;
        }

        .upload-zone:hover {
          border-color: #3B82F6;
          background: #F0F9FF;
        }

        .upload-zone svg {
          color: #3B82F6;
          margin-bottom: 16px;
        }

        .upload-zone h3 {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .upload-zone p {
          margin: 0 0 16px 0;
          font-size: 14px;
          color: #64748B;
        }

        .file-types {
          display: inline-block;
          padding: 4px 12px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          font-size: 12px;
          color: #64748B;
        }

        .actions-row {
          margin-top: 24px;
          text-align: center;
        }

        .btn-template {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          background: white;
          color: #3B82F6;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-template:hover {
          background: #F0F9FF;
          border-color: #3B82F6;
        }

        /* STEP 2: Preview */
        .step-preview {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .preview-header-section {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .preview-title {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .preview-title svg {
          color: #3B82F6;
        }

        .preview-title h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .preview-title p {
          margin: 4px 0 0 0;
          font-size: 13px;
          color: #64748B;
        }

        .preview-stats {
          display: flex;
          gap: 16px;
        }

        .stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 12px 20px;
          background: #F9FAFB;
          border-radius: 8px;
          border: 1px solid #E5E7EB;
        }

        .stat-card.error {
          background: #FEF2F2;
          border-color: #FECACA;
        }

        .stat-label {
          font-size: 12px;
          color: #64748B;
          margin-bottom: 4px;
        }

        .stat-value {
          font-size: 24px;
          font-weight: 700;
          color: #1A1A1A;
        }

        .stat-card.error .stat-value {
          color: #DC2626;
        }

        .validation-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 8px;
          color: #DC2626;
          font-size: 14px;
          font-weight: 500;
        }

        .products-summary-section {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .products-summary-section h3 {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }

        .summary-card {
          padding: 16px;
          background: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .summary-card:hover {
          border-color: #3B82F6;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
        }

        .summary-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .summary-barcode {
          font-family: monospace;
          font-size: 13px;
          font-weight: 600;
          color: #3B82F6;
        }

        .summary-badge {
          padding: 3px 8px;
          background: #10B981;
          color: white;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .summary-desc {
          font-size: 13px;
          color: #64748B;
          margin-bottom: 8px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .summary-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: #64748B;
        }

        .summary-footer strong {
          font-size: 14px;
          color: #1A1A1A;
        }

        .table-section {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .table-header-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .table-header-bar h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .btn-change-file {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          background: white;
          color: #64748B;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-change-file:hover {
          background: #F9FAFB;
          border-color: #3B82F6;
          color: #3B82F6;
        }

        .table-wrapper {
          overflow-x: auto;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .data-table thead {
          position: sticky;
          top: 0;
          background: #F8FAFC;
          z-index: 10;
        }

        .data-table th {
          padding: 12px 8px;
          text-align: left;
          font-weight: 600;
          color: #64748B;
          border-bottom: 1px solid #E5E7EB;
          white-space: nowrap;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .required-col {
          background: #FFFBEB;
        }

        .col-stock {
          background: #EFF6FF;
        }

        .required-mark {
          color: #DC2626;
          margin-left: 4px;
        }

        .col-status,
        .col-actions {
          width: 80px;
          text-align: center;
        }

        .data-table td {
          padding: 8px;
          border-bottom: 1px solid #F1F5F9;
        }

        .row-error {
          background: #FEF2F2;
        }

        .cell-status {
          text-align: center;
        }

        .status-error,
        .status-success {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-weight: 600;
          font-size: 12px;
        }

        .status-error {
          color: #DC2626;
        }

        .status-success {
          color: #10B981;
        }

        .cell-input {
          width: 100%;
          padding: 6px 8px;
          border: 1px solid #E5E7EB;
          border-radius: 4px;
          font-size: 13px;
          min-width: 120px;
        }

        .cell-input:focus {
          outline: none;
          border-color: #3B82F6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .cell-stock {
          text-align: center;
          background: #EFF6FF;
        }

        .stock-badge {
          padding: 4px 10px;
          background: #3B82F6;
          color: white;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          display: inline-block;
        }

        .cell-actions {
          text-align: center;
        }

        .btn-delete-row {
          padding: 6px;
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 6px;
          color: #DC2626;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
        }

        .btn-delete-row:hover {
          background: #DC2626;
          color: white;
        }

        .errors-section {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .errors-section h3 {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
          color: #DC2626;
        }

        .errors-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .error-card {
          padding: 12px;
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 8px;
          font-size: 13px;
        }

        .error-card strong {
          color: #991B1B;
          display: block;
          margin-bottom: 8px;
        }

        .error-card ul {
          margin: 0;
          padding-left: 20px;
          color: #DC2626;
        }

        .step-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .btn-secondary,
        .btn-primary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary {
          background: white;
          border: 1px solid #E5E7EB;
          color: #64748B;
        }

        .btn-secondary:hover {
          background: #F9FAFB;
          border-color: #CBD5E1;
        }

        .btn-primary {
          background: #3B82F6;
          border: 1px solid #3B82F6;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #2563EB;
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* STEP 3: Processing */
        .step-processing {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 500px;
        }

        .processing-container {
          text-align: center;
          max-width: 400px;
        }

        .processing-spinner {
          width: 120px;
          height: 120px;
          margin: 0 auto 32px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .spinner-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border: 4px solid #E5E7EB;
          border-top: 4px solid #3B82F6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .processing-spinner svg {
          color: #3B82F6;
          z-index: 1;
        }

        .processing-container h2 {
          margin: 0 0 8px 0;
          font-size: 24px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .processing-container p {
          margin: 0 0 24px 0;
          font-size: 14px;
          color: #64748B;
        }

        .progress-bar-full {
          height: 8px;
          background: #E5E7EB;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 12px;
        }

        .progress-fill {
          height: 100%;
          background: #3B82F6;
          transition: width 0.3s ease;
        }

        .progress-text {
          font-size: 14px;
          font-weight: 600;
          color: #3B82F6;
        }

        /* STEP 4: Results */
        .step-results {
          max-width: 1200px;
          margin: 0 auto;
        }

        .results-container {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .results-header {
          text-align: center;
          background: white;
          border-radius: 12px;
          padding: 48px 32px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .success-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 24px;
          background: linear-gradient(135deg, #10B981, #059669);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
        }

        .results-header h2 {
          margin: 0 0 8px 0;
          font-size: 28px;
          font-weight: 700;
          color: #1A1A1A;
        }

        .results-header p {
          margin: 0;
          font-size: 14px;
          color: #64748B;
        }

        .results-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .stat-box {
          background: white;
          border-radius: 12px;
          padding: 24px;
          text-align: center;
          border: 2px solid #E5E7EB;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .stat-box.success {
          border-color: #10B981;
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(16, 185, 129, 0.02));
        }

        .stat-box.info {
          border-color: #06B6D4;
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.05), rgba(6, 182, 212, 0.02));
        }

        .stat-box.primary {
          border-color: #3B82F6;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(59, 130, 246, 0.02));
        }

        .stat-box.error {
          border-color: #DC2626;
          background: linear-gradient(135deg, rgba(220, 38, 38, 0.05), rgba(220, 38, 38, 0.02));
        }

        .stat-number {
          display: block;
          font-size: 36px;
          font-weight: 700;
          color: #1A1A1A;
          margin-bottom: 8px;
        }

        .stat-box.success .stat-number {
          color: #10B981;
        }

        .stat-box.info .stat-number {
          color: #06B6D4;
        }

        .stat-box.primary .stat-number {
          color: #3B82F6;
        }

        .stat-box.error .stat-number {
          color: #DC2626;
        }

        .stat-label {
          font-size: 13px;
          color: #64748B;
          font-weight: 500;
        }

        .results-details {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .results-details h3 {
          margin: 0 0 20px 0;
          font-size: 18px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .lotes-results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 16px;
        }

        .lote-result-card {
          border: 1px solid #E5E7EB;
          border-radius: 10px;
          overflow: hidden;
          transition: all 0.2s ease;
        }

        .lote-result-card:hover {
          border-color: #3B82F6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
        }

        .lote-result-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: linear-gradient(135deg, #F8FAFC, #EFF6FF);
          border-bottom: 1px solid #E5E7EB;
        }

        .lote-result-header svg {
          color: #3B82F6;
        }

        .lote-barcode {
          font-family: monospace;
          font-size: 14px;
          font-weight: 600;
          color: #3B82F6;
        }

        .lote-result-body {
          padding: 16px;
        }

        .lote-desc {
          margin: 0 0 12px 0;
          font-size: 13px;
          color: #64748B;
        }

        .lote-stats {
          display: flex;
          gap: 16px;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #E5E7EB;
        }

        .lote-stat {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .lote-stat span {
          font-size: 11px;
          color: #94A3B8;
        }

        .lote-stat strong {
          font-size: 14px;
          color: #1A1A1A;
        }

        .lote-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .lote-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: #F9FAFB;
          border-radius: 6px;
          font-size: 12px;
        }

        .lote-item svg {
          color: #10B981;
          flex-shrink: 0;
        }

        .lote-item span:first-of-type {
          flex: 1;
          color: #1A1A1A;
          font-weight: 500;
        }

        .lote-stock {
          color: #3B82F6;
          font-weight: 600;
        }

        .results-actions {
          display: flex;
          justify-content: center;
          gap: 16px;
          padding: 24px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        @media (max-width: 768px) {
          .steps-container {
            overflow-x: auto;
          }

          .step-info {
            display: none;
          }

          .preview-header-section {
            flex-direction: column;
            gap: 16px;
          }

          .preview-stats {
            width: 100%;
            justify-content: space-between;
          }

          .results-stats {
            grid-template-columns: 1fr;
          }

          .lotes-results-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}

export default BulkUploadModal